import React, { type ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';
import useThemeMode from 'hooks/useThemeMode';
import GlobalStyles from 'themes/GlobalStyles';
import { themeKeyToHint, writeThemeHint } from 'themes/themeHint';
import { getIsThemeKeyLoaded, getThemeKey, getThemeByKey } from 'themes/themesSelectors';
import { AdapterLuxonWrapper } from 'utils/AdapterLuxonWrapper';

const CombinedStyleProviders = ({ children }: { children?: ReactNode }): React.JSX.Element => {
  // react-redux's useSelector, not hooks/redux: tests that mock hooks/redux still render inside these providers.
  const themeKey = useSelector(getThemeKey);
  const themeKeyLoaded = useSelector(getIsThemeKeyLoaded);
  const themeMode = useThemeMode();

  const theme = getThemeByKey(themeMode === 'dark' ? 'ACME_DARK' : 'muiDefault');

  // The next startup paints this before the preferences load.
  React.useEffect(() => {
    if (themeKeyLoaded) writeThemeHint(themeKeyToHint(themeKey));
  }, [themeKey, themeKeyLoaded]);

  const dateAdapter =
    navigator.language.endsWith('US') || navigator.language.endsWith('CA') ? AdapterLuxonWrapper : AdapterLuxon;
  React.useEffect(() => {
    const { mode } = theme.palette;
    document.body.setAttribute('data-theme', mode);
    document.body.setAttribute('data-color-scheme', mode);
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${mode}-mode`);
    document.body.style.fontFamily = theme.typography.fontFamily;
  }, [theme.palette, theme.typography.fontFamily]);

  return (
    <StyledEngineProvider injectFirst>
      <EmotionThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <CssBaseline enableColorScheme />
          <StyledComponentsThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={dateAdapter}>{children}</LocalizationProvider>
          </StyledComponentsThemeProvider>
        </ThemeProvider>
      </EmotionThemeProvider>
      <GlobalStyles />
    </StyledEngineProvider>
  );
};

export default CombinedStyleProviders;
